import { Thread, Message } from '../types/Thread';
import * as ThreadsAPI from '../api/Threads';

/**
 * Loads all threads from the database.
 */
export async function loadThreads(): Promise<Thread[]> {
    try {
        return await ThreadsAPI.fetchThreads();
    } catch (error) {
        console.error('Error loading threads from database:', error);
        // Return empty array instead of throwing to prevent UI from breaking
        return [];
    }
}

/**
 * Saves all threads to the database.
 * Note: This function is deprecated. Use createThread, addMessageToThread, etc. instead.
 */
export function saveThreads(threads: Thread[]): void {
    // Deprecated - individual operations should use the API directly
    console.warn('saveThreads is deprecated - use individual API calls instead');
}

/**
 * Creates a new thread and saves it to the database.
 */
export async function createThread(
    title: string,
    corpusId?: string,
    paperTitle?: string,
    username?: string
): Promise<Thread> {
    const threadId = generateThreadId();
    
    try {
        const thread = await ThreadsAPI.createThread(
            threadId,
            title,
            corpusId,
            paperTitle
        );
        return thread;
    } catch (error) {
        console.error('Error creating thread:', error);
        throw error;
    }
}

/**
 * Adds a message to an existing thread.
 */
export async function addMessageToThread(
    threadId: string,
    message: Message
): Promise<Thread | null> {
    try {
        await ThreadsAPI.addMessage(threadId, message);
        // Fetch the updated thread with all messages
        const thread = await ThreadsAPI.fetchThread(threadId);
        return thread;
    } catch (error) {
        console.error(`Error adding message to thread ${threadId}:`, error);
        return null;
    }
}

/**
 * Updates an existing thread.
 * Note: This function currently only updates locally. For database updates,
 * use addMessageToThread which updates the timestamp automatically.
 */
export function updateThread(updatedThread: Thread): void {
    // Deprecated - updates should happen via addMessageToThread
    console.warn('updateThread is deprecated - thread updates happen via API calls');
}

/**
 * Deletes a thread by ID.
 */
export async function deleteThread(threadId: string): Promise<void> {
    try {
        await ThreadsAPI.deleteThread(threadId);
    } catch (error) {
        console.error(`Error deleting thread ${threadId}:`, error);
        throw error;
    }
}

/**
 * Gets a single thread by ID.
 */
export async function getThread(threadId: string): Promise<Thread | null> {
    try {
        const thread = await ThreadsAPI.fetchThread(threadId);
        return thread;
    } catch (error) {
        console.error(`Error fetching thread ${threadId}:`, error);
        return null;
    }
}

/**
 * Generates a unique thread ID.
 */
function generateThreadId(): string {
    return `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generates a unique message ID.
 */
export function generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generates a thread title from a question (truncates if too long).
 */
export function generateThreadTitle(question: string, maxLength: number = 60): string {
    if (question.length <= maxLength) {
        return question;
    }
    return question.substring(0, maxLength - 3) + '...';
}
